import React from 'react';
import { Button } from 'antd';
import { css, useTheme } from '@emotion/react';
import Tooltip from 'components/common/tooltip';
import styled from 'types/theme.types';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';

/**
 * Two DOM shapes behind the switch: Figma specs a real button, and swapping the legacy span too would
 * change the switch-off DOM (D7.5). `data-e2e` lands on both. APO-484 drops the span.
 */
export const IconButton: React.FunctionComponent<{
  /** Omit where Figma specs no tooltip. Adding one changes switch-off too, since it wraps both branches. */
  tooltipTitleId?: string;
  icon: React.FC<Omit<AntdIconProps, 'ref'> & React.RefAttributes<HTMLSpanElement>>;
  dataE2e: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  /** Legacy takes its name from the icon element; a button wrapping the icon needs this or it is unnamed. */
  ariaLabel?: string;
  /** For a button that discloses content (the tree chevron): the aria-expanded state. */
  ariaExpanded?: boolean;
  /** Forwarded so a call site can extend this with `styled(IconButton)` instead of a wrapper div. */
  className?: string;
}> = ({
  tooltipTitleId,
  icon: Icon,
  dataE2e,
  onClick,
  disabled,
  size = 'middle',
  ariaLabel,
  ariaExpanded,
  className
}) => {
  const theme = useTheme();

  const button = theme.isNewDesign ? (
    <StyledIconButton
      type="text"
      size={size}
      icon={<Icon />}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      data-e2e={dataE2e}
      onClick={onClick}
      disabled={disabled}
      className={className}
    />
  ) : (
    <StyledIcon as={Icon} data-e2e={dataE2e} onClick={onClick} disabled={disabled} className={className} />
  );

  if (!tooltipTitleId) return button;

  return (
    <Tooltip titleId={tooltipTitleId} placement="top">
      {button}
    </Tooltip>
  );
};

/**
 * Figma's Icon button at Size=Small. Every value is stated because antd's own small button differs on
 * all four, and tripled because `.ant-btn.ant-btn-sm.ant-btn-icon-only` weighs three classes.
 *
 * Scoped to this size so the other call sites keep antd's middle geometry.
 */
const smallIconButton = css`
  &&& {
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: var(--padding-xxs);
    border: none;
    border-radius: var(--border-radius-100);

    .anticon {
      font-size: 16px;
    }
  }
`;

const StyledIconButton = styled(Button)`
  color: var(--icon-primary-standalone);

  &:not(:disabled):hover {
    color: var(--icon-primary-hover);
  }

  /* A token hover surface: antd's derived colorBgTextHover is a 6% black that barely registers on a
     card, leaving icon-only actions with no affordance (UI-polish review). Doubled class because antd
     declares the hover background at two classes and its tag is injected later. */
  &&:not(:disabled):hover {
    background-color: var(--surface-secondary-action-hover);
  }

  &:disabled {
    color: var(--icon-disabled);
  }

  ${({ size }) => size === 'small' && smallIconButton}
`;

// Switch-off only, so it needs no branch: the new design never renders it.
const StyledIcon = styled.span<{ disabled?: boolean }>`
  font-size: 18px;
  right: 20px;
  top: 20px;
  margin-right: 10px;
  transition: transform 0.2s;
  :hover {
    transform: scale(1.2);
  }
`;
